/**
 * List all portal sessions stored in localStorage.
 *
 * Each `touchpoint_portal_token_<clientId>` represents a portal session the
 * user has actively signed into on this device. Pairs the token with its
 * role and cached client name so callers (e.g. the creator client
 * switcher) can render a workspace list without hitting the backend.
 *
 * Only sessions whose JWT still validates (correct type, not expired) for
 * the matching clientId are returned — stale entries are skipped.
 */

import { getPortalAuth, getPortalTokenKey } from './storage';
import type { PortalAuthData } from './storage';
import { validatePortalToken } from './tokenUtils';

export interface PortalSessionSummary {
  /** Client workspace UUID (the portal session is scoped to this). */
  clientId: string;
  /** Portal role for this session. Typically 'client' or 'creator'. */
  role: string;
  /** Cached client display name, if available. */
  clientName: string | null;
}

const TOKEN_KEY_PREFIX = 'touchpoint_portal_token_';

interface StoredSessionHit {
  clientId: string;
  stored: PortalAuthData;
}

/** Every touchpoint_portal_token_* entry with a role, no validity check. */
function scanStoredSessions(): StoredSessionHit[] {
  const hits: StoredSessionHit[] = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(TOKEN_KEY_PREFIX)) continue;

      const clientId = key.slice(TOKEN_KEY_PREFIX.length);
      if (!clientId) continue;

      // Defensive: make sure the key is actually the token key (not e.g. a
      // future `touchpoint_portal_token_meta_<id>`).
      if (key !== getPortalTokenKey(clientId)) continue;

      const stored = getPortalAuth(clientId);
      if (!stored) continue;

      hits.push({ clientId, stored });
    }
  } catch (error) {
    console.error('[listPortalSessions] localStorage scan failed:', error);
  }
  return hits;
}

function toSummary({ clientId, stored }: StoredSessionHit): PortalSessionSummary {
  return {
    clientId,
    role: stored.role,
    clientName: stored.clientName ?? null,
  };
}

// Stable alpha sort by display name (falls back to clientId) so lists
// don't reshuffle between renders.
function sortByLabel(sessions: PortalSessionSummary[]): PortalSessionSummary[] {
  sessions.sort((a, b) => {
    const aLabel = (a.clientName || a.clientId).toLowerCase();
    const bLabel = (b.clientName || b.clientId).toLowerCase();
    return aLabel.localeCompare(bLabel);
  });
  return sessions;
}

/**
 * Return every valid portal session found in localStorage, optionally
 * filtered by role.
 *
 * @param filterRole When provided, only sessions with this role are returned.
 */
export function listPortalSessions(filterRole?: string): PortalSessionSummary[] {
  const sessions = scanStoredSessions()
    .filter(({ stored }) => !filterRole || stored.role === filterRole)
    .filter(({ clientId, stored }) => validatePortalToken(stored.token, clientId).valid)
    .map(toSummary);
  return sortByLabel(sessions);
}

/**
 * Launch-time session presence (TD-016): a session counts when its access
 * token is valid OR merely expired with a refresh token stored — the
 * target surface's auth (portalAuthLoader / usePortalAuth) refreshes
 * transparently. Without this, every PWA launch more than an access-token
 * TTL after last use bounced to login despite a 30-day refresh token.
 */
export function listRefreshablePortalSessions(): PortalSessionSummary[] {
  const sessions = scanStoredSessions()
    .filter(({ clientId, stored }) => {
      const validation = validatePortalToken(stored.token, clientId);
      if (validation.valid) return true;
      return validation.reason === 'expired' && Boolean(stored.refreshToken);
    })
    .map(toSummary);
  return sortByLabel(sessions);
}
